import React from "react";
import { Link } from "react-router-dom";
import { Scale, ArrowLeft } from "lucide-react";
import { LandingFooter } from "@/components/landing/CtaBanner";

// The only contact address currently configured for the service (ADMIN_EMAIL).
const CONTACT_EMAIL = "supravat111@gmail.com";
const EFFECTIVE_DATE = "12 September 2026";

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`terms-section-${id}`} className="scroll-mt-24">
    <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">{title}</h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="ml-1 space-y-2.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span aria-hidden="true" className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const TOC = [
  ["acceptance", "1. Acceptance of these terms"],
  ["service", "2. What Advocate Diary is"],
  ["eligibility", "3. Eligibility and account"],
  ["accuracy", "4. Accurate information and account security"],
  ["acceptable", "5. Acceptable use"],
  ["prohibited", "6. Prohibited activities"],
  ["ip", "7. Our software, branding and platform content"],
  ["your-data", "8. Your data — matters, clients and documents"],
  ["portal", "9. Client portal responsibilities"],
  ["plans", "10. Free trial, free plan and paid plans"],
  ["billing", "11. Pricing and billing through Razorpay"],
  ["cancellation", "12. Cancellation and refunds"],
  ["availability", "13. Service availability"],
  ["privacy", "14. Data and privacy"],
  ["documents", "15. Documents and storage"],
  ["no-advice", "16. No legal advice"],
  ["liability", "17. Limitation of liability"],
  ["suspension", "18. Suspension and termination"],
  ["changes", "19. Changes to the service and these terms"],
  ["law", "20. Governing law"],
  ["contact", "21. Contact"],
];

export default function Terms() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="terms-brand" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
              <Scale className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl tracking-tight text-navy">Advocate Diary</span>
          </Link>
          <Link
            to="/"
            data-testid="terms-back-home"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy/70 transition-colors hover:bg-secondary hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      <div className="border-b border-border bg-navy px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold">Legal</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Terms of Service
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            The terms that apply when you use the Advocate Diary application.
          </p>
          <p className="mt-6 text-xs text-slate-400" data-testid="terms-effective-date">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>
      </div>

      <main className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[220px_minmax(0,1fr)]">
          <nav data-testid="terms-toc" className="hidden lg:block">
            <div className="sticky top-24">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                On this page
              </p>
              <ul className="mt-4 space-y-2 border-l border-border pl-4">
                {TOC.map(([id, label]) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      data-testid={`terms-toc-${id}`}
                      className="block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-l border-border pl-4">
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                  Related
                </p>
                <Link
                  to="/privacy-policy"
                  data-testid="terms-toc-privacy"
                  className="mt-3 block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </nav>

          <article className="max-w-3xl space-y-12">
            <Section id="acceptance" title="1. Acceptance of these terms">
              <p>
                These Terms of Service (the &ldquo;Terms&rdquo;) apply when you access or use the Advocate Diary
                application, whether on the free plan, the trial or a paid plan. By creating an account, signing
                in or using the service, you agree to be bound by these Terms.
              </p>
              <p>
                If you are using Advocate Diary on behalf of a chamber or law firm, you confirm that you are
                authorised to accept these Terms on behalf of that firm, and &ldquo;you&rdquo; in these Terms
                means both you individually and the firm.
              </p>
              <p>
                If you do not agree to these Terms, do not create an account and do not use the service.
              </p>
            </Section>

            <Section id="service" title="2. What Advocate Diary is">
              <p>
                Advocate Diary is a software-as-a-service application built for advocates, chambers and law
                firms in India to manage their day-to-day practice. It provides tools for:
              </p>
              <Bullets
                items={[
                  "Case and matter management, including case details, parties, courts and status.",
                  "Hearing tracking, adjournments, outcomes and a court diary view.",
                  "Client records, contact details and a read-only client portal.",
                  "Tasks, reminders and internal notes on matters.",
                  "Document upload, attachment to matters and download by authorised users.",
                  "Reports, team management and organisation-level settings.",
                ]}
              />
              <p>
                Advocate Diary is a software service only. It is not a law firm, does not act as an advocate for
                any person and does not provide legal advice.
              </p>
            </Section>

            <Section id="eligibility" title="3. Eligibility and account">
              <p>
                To use Advocate Diary you must be able to enter into a binding agreement under Indian law. If
                you register as a law firm, you must be authorised to act for that firm. If you add team
                members, you must have the right to give each member access to your firm&rsquo;s records.
              </p>
              <p>
                Each account is issued to a specific email address. You are responsible for everything that
                happens under your account, including anything done by team members you add.
              </p>
            </Section>

            <Section id="accuracy" title="4. Accurate information and account security">
              <p>
                You agree to provide accurate information at registration and to keep your profile and firm
                details up to date. In particular:
              </p>
              <Bullets
                items={[
                  "Use a real, working email address that you control, so that password resets and important account emails reach you.",
                  "Keep your password confidential and do not share your sign-in credentials with anyone else. Each team member should have their own account.",
                  "Choose a password of at least eight characters, and do not reuse a password you use on other services.",
                  "Sign out on devices that are not yours, including shared or court-room devices.",
                  "Tell us promptly at the contact address below if you believe your account has been accessed without authorisation.",
                ]}
              />
              <p>
                You remain responsible for activity carried out under your account, including any records
                created, modified or downloaded from it.
              </p>
            </Section>

            <Section id="acceptable" title="5. Acceptable use">
              <p>
                Advocate Diary is provided so that you can run your own practice inside the application. You may
                use the service to record and manage your matters, clients, hearings, tasks, documents and team,
                and to invite your own clients to the read-only portal for their own matter.
              </p>
              <p>
                You agree to use the service in compliance with applicable Indian law and the professional
                conduct obligations that apply to you as an advocate, including duties of confidentiality
                towards your own clients.
              </p>
            </Section>

            <Section id="prohibited" title="6. Prohibited activities">
              <p>You agree not to:</p>
              <Bullets
                items={[
                  "Use the service for any purpose that is unlawful, fraudulent, harmful or that would put another person at risk.",
                  "Upload, store or share content that you do not have the right to store or share, or that infringes another person's rights.",
                  "Upload content that is unlawful, defamatory, obscene, or that contains malware or malicious code.",
                  "Attempt to access another organisation's data, another user's account, platform administration functions, or any part of the service you are not authorised to use.",
                  "Probe, scan, disrupt, reverse engineer, decompile, scrape or interfere with the service, its security, its rate limiting or its infrastructure.",
                  "Resell, sublicense or otherwise commercialise access to the service to third parties outside your firm.",
                  "Use the service in a way that abuses free plans, trials, invitations or any features designed to prevent misuse.",
                ]}
              />
            </Section>

            <Section id="ip" title="7. Our software, branding and platform content">
              <p>
                Advocate Diary, including the application, the source code, the user interface, the layout, the
                designs, the &ldquo;Advocate Diary&rdquo; name, its logo and any marketing content on the public
                site, belong to us and to our licensors. Nothing in these Terms transfers any ownership of the
                service or of our brand to you.
              </p>
              <p>
                We grant you a limited, non-exclusive, non-transferable, revocable right to use the service
                through your account, for your own practice, for the duration of your account and subject to
                these Terms. You may not copy, reproduce or distribute the software, its interface or our
                branding outside of that use.
              </p>
            </Section>

            <Section id="your-data" title="8. Your data — matters, clients and documents">
              <p>
                The information you enter into Advocate Diary — including your cases, clients, hearings, tasks,
                notes, court details, reports and the documents you upload — remains yours. We do not claim
                ownership of what you record about your practice.
              </p>
              <p>
                You are responsible for that information. In particular, you decide what to record about a
                client or a matter, and you are responsible for its accuracy, for having the right to store it,
                and for meeting your own confidentiality and professional obligations towards your clients.
              </p>
              <p>
                We process what you enter in order to provide the service to you and as described in our{" "}
                <Link
                  to="/privacy-policy"
                  data-testid="terms-inline-privacy"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </Section>

            <Section id="portal" title="9. Client portal responsibilities">
              <p>
                You may invite your own client to a read-only portal for a matter you handle for them. When you
                send an invitation, an email is sent to the address you provide. You are responsible for:
              </p>
              <Bullets
                items={[
                  "Sending the invitation only to the person entitled to see the matter, at an email address you have verified with that client.",
                  "Choosing what to publish to the portal — the client sees only what you choose to make visible for their own matter.",
                  "Revoking access when it is no longer appropriate, such as when the engagement ends.",
                ]}
              />
              <p>
                The portal is limited by design: a client cannot see your internal notes, other clients or
                firm-wide data. However, you are responsible for the fact that you invited a particular person
                and for what you publish to them.
              </p>
            </Section>

            <Section id="plans" title="10. Free trial, free plan and paid plans">
              <p>
                Advocate Diary is offered on the plans made available inside the application at the time you
                subscribe. Plan features, limits and prices are shown in the application and may change over
                time.
              </p>
              <Bullets
                items={[
                  "A free plan and a trial may be offered so you can evaluate the service before subscribing.",
                  "A paid plan gives you access to the features and limits associated with that plan for the paid period.",
                  "Advocate Diary may add, remove or change plan features and limits from time to time. Where such a change materially affects the plan you are on, the change will be reflected in the application.",
                ]}
              />
            </Section>

            <Section id="billing" title="11. Pricing and billing through Razorpay">
              <p>
                Subscriptions are processed through <strong className="text-navy">Razorpay</strong>. When you
                subscribe, you are taken to Razorpay&rsquo;s checkout to complete the payment.
              </p>
              <Bullets
                items={[
                  "Advocate Diary does not store your card number, CVV, UPI PIN, net banking credentials or any other payment instrument credentials. These are entered into and handled by Razorpay, not by Advocate Diary.",
                  "The price applicable to your plan is the price shown in the application at the time you subscribe. Applicable taxes may be added at checkout.",
                  "You authorise Advocate Diary and Razorpay to charge the amount payable for the plan you select, for the billing cycle applicable to that plan.",
                  "Razorpay's handling of your payment details is governed by Razorpay's own terms and privacy policy.",
                ]}
              />
              <p>
                Payment records — for example the plan you are on, the status of the subscription, and the
                payments made — are stored so that your account, receipts and access to features are correct.
              </p>
            </Section>

            <Section id="cancellation" title="12. Cancellation and refunds">
              <p>
                You can cancel a paid subscription from Settings inside the application. Cancellation stops
                further renewals of your plan.
              </p>
              <p>
                Refund eligibility, timelines and the process for requesting a refund are set out in our
                separate{" "}
                <Link
                  to="/refund-policy"
                  data-testid="terms-inline-refund"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Cancellation &amp; Refund Policy
                </Link>
                , which forms part of these Terms.
              </p>
            </Section>

            <Section id="availability" title="13. Service availability">
              <p>
                We work to keep Advocate Diary available and usable, but the service is provided on an
                &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. We do not guarantee that the service
                will be uninterrupted, error-free or continuously available.
              </p>
              <Bullets
                items={[
                  "The service may be unavailable from time to time for planned maintenance, upgrades, security work, or reasons outside our reasonable control such as issues at hosting, database, storage, email or payment providers, or connectivity failures.",
                  "We do not publish or promise any specific uptime service level.",
                  "You are responsible for keeping your own records of matters and hearings that are important to you and for meeting your own court-imposed deadlines.",
                ]}
              />
            </Section>

            <Section id="privacy" title="14. Data and privacy">
              <p>
                The information collected by the application and how it is handled is described in our{" "}
                <Link
                  to="/privacy-policy"
                  data-testid="terms-inline-privacy-2"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Privacy Policy
                </Link>
                , which forms part of these Terms.
              </p>
              <p>
                Each organisation&rsquo;s records are scoped to that organisation, and access within a firm is
                controlled by the roles set by the firm administrator. You are responsible for who you add to
                your firm and for the role you give them.
              </p>
            </Section>

            <Section id="documents" title="15. Documents and storage">
              <p>
                You can upload documents and attach them to your matters. Uploaded files are stored using
                Cloudflare R2 object storage, which the application uses as its document store. Access to a
                document is limited to users of the organisation the document belongs to, and each download is
                authorised against your signed-in account.
              </p>
              <Bullets
                items={[
                  "Do not upload files that you do not have the right to store, or that contain malware, or that violate applicable law.",
                  "Storage and file-size limits associated with your plan are shown in the application. We may enforce those limits to keep the service running for everyone.",
                  "When you delete a document in the application, it is marked as deleted and is no longer available through the application.",
                ]}
              />
            </Section>

            <Section id="no-advice" title="16. No legal advice">
              <p>
                Advocate Diary is a practice management tool. Nothing in the application, its interface, its
                templates, its notifications or any content associated with it is legal advice, and using the
                application does not create an advocate-client relationship between you and Advocate Diary.
              </p>
              <p>
                You remain solely responsible for the legal work you carry out for your clients, for the
                accuracy of what you record about a matter, and for the professional obligations that apply to
                you as an advocate.
              </p>
            </Section>

            <Section id="liability" title="17. Limitation of liability">
              <p>
                To the maximum extent permitted by applicable law, Advocate Diary is not liable for indirect,
                incidental, special, consequential, exemplary or punitive damages, loss of profits, loss of
                revenue, loss of goodwill, loss of clients, loss of use, or loss of data, arising out of or in
                connection with your use of the service, even if we have been advised of the possibility of
                such damages.
              </p>
              <p>
                To the maximum extent permitted by applicable law, our total aggregate liability to you for all
                claims arising out of or in connection with the service or these Terms is limited to the
                amounts actually paid by you to us for the service during the three months immediately
                preceding the event giving rise to the claim.
              </p>
              <p>
                Nothing in these Terms limits any liability that cannot be limited under applicable law.
              </p>
            </Section>

            <Section id="suspension" title="18. Suspension and termination">
              <p>
                We may suspend or terminate your access to the service, in whole or in part, and with or
                without notice, if:
              </p>
              <Bullets
                items={[
                  "You breach these Terms, including the prohibited activities above.",
                  "Your use of the service creates a risk to the service, to other users, or to the security of the platform.",
                  "You do not pay amounts due for a paid plan.",
                  "We are required to do so by law or by a competent authority.",
                ]}
              />
              <p>
                You can stop using the service at any time. On termination, your right to use the service
                ends. Sections of these Terms that by their nature should survive termination — including the
                sections on your data, our software and branding, no legal advice, limitation of liability and
                governing law — will continue to apply.
              </p>
            </Section>

            <Section id="changes" title="19. Changes to the service and these terms">
              <p>
                We may change the service from time to time — for example, we may add, modify or remove
                features, adjust plan limits, change the way a feature works, or change the third-party
                services we rely on. Where a change materially affects a plan you are on, the change will be
                reflected in the application.
              </p>
              <p>
                We may also update these Terms as the service changes. The effective date at the top of this
                page shows when the current version took effect. Where a change materially affects your rights
                or obligations, the updated Terms will be made available on this page before continuing to
                operate under them. Continuing to use the service after an update means the updated Terms
                apply to your use of it.
              </p>
            </Section>

            <Section id="law" title="20. Governing law">
              <p>
                These Terms and any dispute or claim arising out of or in connection with them, the service or
                your use of it, are governed by the laws of India, without reference to conflict of laws
                principles. Applicable Indian law includes, among others, the Indian Contract Act, 1872, the
                Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023, in each
                case as applicable.
              </p>
              <p>
                No particular court or place of jurisdiction is specified in these Terms.
              </p>
            </Section>

            <Section id="contact" title="21. Contact">
              <p>For any question about these Terms, or for support relating to the service, contact us at:</p>
              <p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="terms-contact-email"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                Please write from the email address registered on your account, so that we can be reasonably
                satisfied the request comes from you. No telephone number or postal address is published for
                support, and none should be assumed. Email is the contact route for the service.
              </p>
            </Section>

            <div className="rounded-lg border border-border bg-secondary/60 p-5">
              <p className="text-xs leading-relaxed text-muted-foreground">
                These Terms describe the Advocate Diary service as currently operated. They intentionally do
                not state a legal entity name, registered address, telephone number, jurisdiction, uptime
                commitment, refund promise, certification or data-retention period, because none of those has
                been established or configured for the service. This page will be updated if that changes.
                Please also read our{" "}
                <Link
                  to="/privacy-policy"
                  data-testid="terms-footer-privacy"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Privacy Policy
                </Link>
                , which forms part of these Terms.
              </p>
            </div>
          </article>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
